import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import getAPIbaseURL from "../../utils/helpers";

const API_URL = `${getAPIbaseURL()}/messages`;

export const fetchMessages = createAsyncThunk(
    "messages/fetchMessages",
    async (_, thunkAPI) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data || "Error fetching messages");
      }
    }
  );

export const fetchMessageById = createAsyncThunk(
    "messages/fetchMessageById",
    async (messageId, thunkAPI) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/${messageId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data || "Error fetching message");
      }
    }
  );

export const createDraftMessage = createAsyncThunk(
    "messages/createDraftMessage",
    async (messageData, thunkAPI) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(`${API_URL}/draft`, messageData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data || "Error creating draft message");
      }
    }
  );

export const sendMessage = createAsyncThunk(
    "messages/sendMessage",
    async (messageId, thunkAPI) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(`${API_URL}/send/${messageId}`, null, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data || "Error sending message");
      }
    }
  );

export const updateMessage = createAsyncThunk(
    "messages/updateMessage",
    async ({ messageId, updateData }, thunkAPI) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.put(`${API_URL}/${messageId}`, updateData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        return response.data;
      } catch (error) {
        return thunkAPI.rejectWithValue(
          error.response?.data || "Error updating message"
        );
      }
    }
  );  

export const deleteMessage = createAsyncThunk(
    "messages/deleteMessage",
    async (messageId, thunkAPI) => {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${API_URL}/${messageId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return messageId;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data || "Error deleting message");
      }
    }
  );

export const fetchMessagesByStatus = createAsyncThunk(
    "messages/fetchMessagesByStatus",
    async ({ status }, thunkAPI) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/status/${status}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data || "Error fetching messages by status");
      }
    }
  );

  export const fetchUnreadCount = createAsyncThunk(
    "messages/fetchUnreadCount",
    async (_, thunkAPI) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/unread/count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data.unread_count;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data || "Error fetching unread count");
      }
    }
  );
  
  export const markMessageAsRead = createAsyncThunk(
    "messages/markMessageAsRead",
    async (messageId, thunkAPI) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.put(`${API_URL}/${messageId}/read`, null, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data || "Error marking message as read");
      }
    }
  );  

const initialState = {
    items: [],
    inbox: [],
    sent: [],
    unreadCount: 0,
    currentMessage: null,
    filtered: [],
    isLoading: false,
    error: null,
    message: null,
  };

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    clearMessageState: (state) => {
      state.message = null;
      state.error = null;
    },
    clearCurrentMessage: (state) => {
      state.currentMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchMessageById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessageById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentMessage = action.payload;
      })
      .addCase(fetchMessageById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createDraftMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDraftMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items.push(action.payload);
        state.message = "Draft message created successfully.";
      })
      .addCase(createDraftMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.map((msg) =>
          msg.id === action.payload.id ? action.payload : msg
        );
        state.message = "Message sent successfully.";
        state.currentMessage = null;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.map((msg) =>
          msg.id === action.payload.id ? action.payload : msg
        );
        state.message = "Message updated successfully.";
      })
      .addCase(updateMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.filter((msg) => msg.id !== action.payload);
        state.message = "Message deleted successfully.";
        state.currentMessage = null;
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchMessagesByStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessagesByStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.meta.arg.status === "sent") {
            state.sent = action.payload;
          }
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(markMessageAsRead.fulfilled, (state, action) => {
        const index = state.items.findIndex((msg) => msg.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (!state.items[index].is_read) {
          state.unreadCount -= 1;
        }
      })            
      .addCase(fetchMessagesByStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearMessageState, clearCurrentMessage } = messagesSlice.actions;
export default messagesSlice.reducer;